import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WikiBattleSprite from './WikiBattleSprite';
import { ItemImage } from '../inventory/inventoryBagGridUtils';
import { metinStoneSheet, METIN_ROW_IDLE } from '../../utils/battleSprites';

const WikiMonsterPopup = ({ monster, onClose }) => {
  const { t } = useTranslation('wiki');

  const spriteSheet = useMemo(() => {
    if (monster?.kind === 'metin' && monster?.id) {
      return metinStoneSheet(monster.id, METIN_ROW_IDLE);
    }
    if (!monster?.battleSprites) return null;
    const bs = typeof monster.battleSprites === 'string'
      ? JSON.parse(monster.battleSprites)
      : monster.battleSprites;
    return bs?.run || bs?.all || bs?.attack || null;
  }, [monster]);

  if (!monster) return null;

  const kindLabel = {
    monster: t('kindMonster'),
    metin: t('kindMetin'),
    boss: t('kindBoss'),
  }[monster.kind] || monster.kind;

  const renderDrop = (drop, idx) => (
    <div key={`${drop.itemType}-${drop.itemId}-${idx}`} className="wiki-drop-row">
      {drop.imageUrl ? (
        <ItemImage imageUrl={drop.imageUrl} alt={drop.name} className="wiki-drop-image" />
      ) : (
        <span className="wiki-drop-image wiki-drop-image--placeholder">?</span>
      )}
      <div className="wiki-drop-info">
        <span className="wiki-drop-name">{drop.name}</span>
        <span className="wiki-drop-rate">
          {t('dropRate')}: %{drop.dropPercent?.toFixed(1)} ({drop.dropRate}‰)
          {drop.countMin > 0 || drop.countMax > 0 ? (
            <> · {t('countRange', { min: drop.countMin || 1, max: drop.countMax || drop.countMin || 1 })}</>
          ) : null}
        </span>
      </div>
    </div>
  );

  return (
    <div className="wiki-popup-backdrop" onClick={onClose} role="presentation">
      <div className="wiki-popup" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="wiki-popup-sticky-top">
          <button type="button" className="wiki-popup-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="wiki-popup-header">
          {spriteSheet ? (
            <WikiBattleSprite
              sheet={spriteSheet}
              playing
              loop
              className="wiki-monster-sprite-inner"
              fallbackLabel={monster.name}
            />
          ) : null}
          <div>
            <span className="wiki-kind-badge">{kindLabel}</span>
            <h3>{monster.name}</h3>
            <p className="wiki-popup-sub">{t('level')}: {monster.level}</p>
          </div>
        </div>

        <div className="wiki-popup-stats">
          <div><strong>{t('attack')}:</strong> {monster.attackMin}–{monster.attackMax}</div>
          <div><strong>{t('defense')}:</strong> {monster.defense}</div>
          <div><strong>{t('hp')}:</strong> {monster.hpMin}{monster.hpMax !== monster.hpMin ? `–${monster.hpMax}` : ''}</div>
          <div><strong>{t('exp')}:</strong> {monster.exp}</div>
          <div><strong>{t('gold')}:</strong> {monster.goldMin}–{monster.goldMax}</div>
          <div><strong>{t('attackSpeed')}:</strong> {monster.attackSpeed}</div>
          <div><strong>{t('steps')}:</strong> {monster.minSteps}–{monster.maxSteps}</div>
          {monster.spawnChance > 0 ? (
            <div><strong>{t('spawnChance')}:</strong> %{monster.spawnChance}</div>
          ) : null}
          {monster.damageType ? (
            <div><strong>{t('damageType')}:</strong> {monster.damageType}</div>
          ) : null}
        </div>

        {monster.waveMonsters?.length > 0 ? (
          <div className="wiki-popup-section">
            <h4>{t('waveMonsters')}</h4>
            <ul className="wiki-wave-list">
              {monster.waveMonsters.map((wm) => (
                <li key={wm.monsterId}>
                  {wm.name} — {wm.weightPercent?.toFixed(1)}%
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="wiki-popup-section">
          <h4>{t('drops')}</h4>
          <p className="wiki-note">{t('dropRateNote')}</p>
          {monster.drops?.length > 0 ? (
            <div className="wiki-drop-list">{monster.drops.map(renderDrop)}</div>
          ) : (
            <p className="wiki-empty">{t('noDrops')}</p>
          )}
        </div>

        {monster.stomachDrops?.length > 0 ? (
          <div className="wiki-popup-section">
            <h4>{monster.kind === 'metin' ? t('coreDrops') : t('stomachDrops')}</h4>
            <div className="wiki-drop-list">{monster.stomachDrops.map(renderDrop)}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WikiMonsterPopup;
